import React, { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation } from "react-query"
import { useParams } from "react-router-dom"

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  useMediaQuery,
  useTheme as useMuiTheme,
} from "@mui/material"
import { Button, NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import type { MRT_ColumnDef } from "material-react-table"

import { BaseTable } from "../../../../components/tables/BaseTable"
import { KonanAPI } from "../../../../services/KonanAPI"
import { queryClient } from "../../../../store/QueryClientStore"
import type { WorkflowResponseFilter } from "../../../../types/generated/api/WorkflowResponseFilter"
import type { WorkflowVersionRetrieve } from "../../../../types/generated/api/WorkflowVersionRetrieve"

interface DialogProps {
  isOpen: boolean
  onClose: () => void
  // Workflow uuid
  uuid: string
  isWorkflowDeprecated?: boolean
  workflow: WorkflowVersionRetrieve
}

/**
 * OutputFiltersDialog component that manages workflow response filters.
 * Allows users to configure which computed features are included in workflow outputs.
 */
export function OutputFiltersDialog(props: Readonly<DialogProps>): React.ReactElement {
  // Prop destructuring
  const { isOpen, onClose, uuid, isWorkflowDeprecated, workflow } = props

  // Navigation hooks
  const { id: projectId } = useParams<{ id: string }>()

  // Theme hooks
  const MuiTheme = useMuiTheme()

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "outputFiltersDialog",
  })

  // State hooks
  const [filters, setFilters] = useState<string[]>([])

  // Memoized values
  const computed_feature_names = useMemo((): { name: string }[] | undefined => {
    const unique: string[] = []

    return workflow?.nodes
      ?.map((node) => {
        if (!unique.includes(node.computed_feature_name)) {
          unique.push(node.computed_feature_name)
          return {
            name: node.computed_feature_name,
          }
        }

        return {
          name: undefined,
        }
      })
      .filter((item) => item.name !== undefined)
  }, [workflow?.nodes])

  const createUpdateOutputFilters = useMutation<
    AxiosResponse<WorkflowResponseFilter>,
    AxiosError,
    {
      project_uuid: string
      workflow_uuid: string
      fields: string[]
    }
  >(KonanAPI.createUpdateOutputFilters, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(["workflow", projectId, uuid])
      await queryClient.resetQueries({ queryKey: ["project-predictions"] })

      NotificationUtils.toast(t("notifications.createUpdateOutputFilters.success"), {
        severity: "success",
      })
    },
    onError: () =>
      NotificationUtils.toast(t("notifications.createUpdateOutputFilters.error"), {
        severity: "error",
      }),
  })

  const handleSubmit = async (): Promise<void> => {
    await createUpdateOutputFilters.mutateAsync({
      project_uuid: projectId as string,
      workflow_uuid: uuid ?? "",
      fields: filters,
    })

    onClose()
  }

  const columns: MRT_ColumnDef<{ name: string; include: string }>[] = [
    {
      header: t("table.headers.name"),
      accessorKey: "name",
    },
    {
      header: t("table.headers.include"),
      accessorKey: "include",
      Cell: ({ row }) => {
        return (
          <Switch
            key={row.original.name}
            checked={!filters.includes(row.original.name)}
            disabled={isWorkflowDeprecated}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              !e.target.checked
                ? setFilters([...filters, row.original.name])
                : setFilters(filters.filter((item) => item !== row.original.name))
            }}
          />
        )
      },
    },
  ]

  // onMount: setting filters to workflow filters or empty array
  useEffect(() => {
    setFilters(workflow.response_filter ?? [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullScreen={useMediaQuery(MuiTheme.breakpoints.down("md"))}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { overflow: "hidden" } }}
    >
      <DialogTitle className="dialog-header-base">
        <Typography variant="h2-bold">{t("title")}</Typography>
      </DialogTitle>

      <DialogContent className={`dialog-content-base `} style={{ height: "440px" }}>
        <BaseTable
          columns={columns as MRT_ColumnDef[]}
          enableOrdering={false}
          data={[
            ...(workflow?.nodes?.find((node) => node.node_type === "taglist") ? [{ name: "KONAN_TAGS" }] : []),
            ...(computed_feature_names ?? []),
          ]}
          title={t("table.title")}
          rowCount={computed_feature_names?.length}
          pageSize={5}
          isLoading={false}
        />
      </DialogContent>

      {!isWorkflowDeprecated && (
        <DialogActions className="dialog-actions-base">
          <Button variant={"secondary"} onClick={onClose} autoFocus>
            {t("buttons.cancel")}
          </Button>

          <Button
            variant={"primary"}
            onClick={handleSubmit}
            id="main_btn"
            isLoading={createUpdateOutputFilters.isLoading}
          >
            {t("buttons.save")}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}
